/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import type * as React from 'react';

import { AlwaysLightMode, Chip, Grid, TabContext, Typography2 } from '@coursera/cds-core';
import type { PageConfigComponent as PageConfigComponentTrackingData } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { buildCarouselForFilter } from 'bundles/cds-labs/components/Modules/CollectionsModule/buildCarouselForFilter';
import type { Filter } from 'bundles/cds-labs/components/Modules/CollectionsModule/types';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/cds-labs';

const TrackedChip = withSingleTracked({ type: 'BUTTON' })(Chip);

const styles = {
  filters: (hasVerticalLayout: boolean) => css`
    margin-bottom: var(--cds-spacing-100);
    ${hasVerticalLayout ? 'margin-top: var(--cds-spacing-150);' : ''}

    && {
      gap: var(--cds-spacing-100);
    }
  `,

  chip: css`
    &.cds-Chip-root {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-neutral-background-primary);
      border-color: var(--cds-color-neutral-disabled-strong);
    }

    &.cds-Chip-selected {
      color: var(--cds-color-blue-800);
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      border-color: var(--cds-color-blue-800);
    }

    &.cds-Chip-root:hover {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-neutral-disabled-weak);
      border-color: var(--cds-color-neutral-primary);
    }
  `,

  imageFilterContainer: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    white-space: nowrap;
  `,

  chipWithAccessoryWrapper: css`
    position: relative;
    display: inline-flex;

    && .cds-Chip-root {
      padding-right: 44px;
    }
  `,

  chipAccessory: css`
    position: absolute;
    right: var(--cds-spacing-50);
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
  `,
  chipAccessorySelected: css`
    &&&& button {
      /* stylelint-disable-next-line custom-property-pattern */
      --iconButton-color: var(--cds-color-neutral-primary-invert);

      color: var(--cds-color-neutral-primary-invert);
    }

    /* cds-core does not export ButtonBase's focus-visible class. */
    &&&& button:hover,
    &&&& button:active,
    &&&& button.cds-buttonBase-focusVisible {
      /* stylelint-disable-next-line custom-property-pattern */
      --iconButton-background: var(--cds-color-interactive-primary-hover);

      color: var(--cds-color-neutral-primary-invert);
    }
  `,
};

type Props = {
  filters: Filter[];
  currentCollectionSetIndex: number;
  isSideBySideLayout: boolean;
  shouldRenderImagesForFilters: boolean;
  customChipStyles?: SerializedStyles;
  onFilterChange?: (index: number, filterName: string) => void;
  collections?: CarouselCollection[];
  /**
   * Parent carousel / section headline for `carousel.name` on filter-chip clicks.
   * Distinct from the chip label (`carousel.filter.name`).
   */
  carouselName?: string;
  eventingData?: {
    pageConfigComponent?: PageConfigComponentTrackingData;
  };
};

const CollectionFilterChips = ({
  filters,
  currentCollectionSetIndex,
  isSideBySideLayout,
  shouldRenderImagesForFilters,
  customChipStyles,
  onFilterChange,
  collections,
  carouselName,
  eventingData,
}: Props) => {
  const trackV3 = useTracker();

  return (
    <TabContext value={currentCollectionSetIndex.toString()}>
      <Grid
        container
        aria-label={_t('Filter By')}
        css={styles.filters(!isSideBySideLayout)}
        role="tablist"
        wrap="wrap"
        direction="row"
      >
        {filters.map(({ filterName, id, filterImage, accessory, eventingData: filterEventingData }, idx) => {
          const hasAccessory = !!accessory;
          const isSelected = currentCollectionSetIndex === idx;
          const chipStyle = shouldRenderImagesForFilters ? styles.chip : customChipStyles;
          let tabContents: React.ReactText;
          if (shouldRenderImagesForFilters) {
            // @ts-expect-error Chip only accepts ReactText, but this JSX works as a child.
            tabContents = (
              <div css={styles.imageFilterContainer}>
                <Image {...filterImage} description="" width={24} height={24} />
                <Typography2 variant="subtitleSmall" component="span">
                  {filterName}
                </Typography2>
              </div>
            );
          } else {
            tabContents = filterName ?? '';
          }

          const handleClick = () => {
            trackV3('click_button', {
              carousel: buildCarouselForFilter({
                id,
                name: carouselName,
                recommender: collections?.[currentCollectionSetIndex]?.recommender,
                filter: filterEventingData?.carouselFilter,
              }),
              button: {
                name: 'collections_filter_chip',
                ...(eventingData?.pageConfigComponent ? { pageConfigComponent: eventingData.pageConfigComponent } : {}),
              },
            });
            onFilterChange?.(idx, filterName);
          };

          const chip = (
            <TrackedChip
              key={id}
              selected={isSelected}
              aria-selected={isSelected}
              aria-checked={undefined}
              onClick={handleClick}
              aria-label={filterName}
              role="tab"
              aria-controls="collections-results"
              trackingName="collections_filter_chip"
              css={chipStyle}
            >
              {tabContents}
            </TrackedChip>
          );

          const chipWithAccessory = hasAccessory ? (
            <span css={styles.chipWithAccessoryWrapper} role="presentation" key={id}>
              {chip}
              <span
                css={[
                  styles.chipAccessory,
                  isSelected && !shouldRenderImagesForFilters && !customChipStyles && styles.chipAccessorySelected,
                ]}
              >
                {accessory}
              </span>
            </span>
          ) : (
            chip
          );

          if (shouldRenderImagesForFilters) {
            return <AlwaysLightMode key={id}>{chipWithAccessory}</AlwaysLightMode>;
          }

          return chipWithAccessory;
        })}
      </Grid>
    </TabContext>
  );
};

export default CollectionFilterChips;
